import {Hash} from "~/interfaces/hashInterfaces";
import Guid from "./guid";
import {ArrayHelper} from "~/helpers/arrayHelper"

export let ObjectHelper = {
  toArray<T>(object: Hash<T>): T[] {
    return Object.keys(object).map(key => object[key]);
  },
  clone<T>(object: Hash<T>): Hash<T> {
    return Object.assign({}, object);
  },
  isPresent<T>(object: Hash<T>): boolean {
    return Object.keys(object).length > 0;
  },
  actOn(rootObj: object, callback: (obj: object, objPropName: string) => void, recurse: boolean = true, opts: { callbackObjects?: boolean } = {}): void {
    for (const propName of Object.getOwnPropertyNames(rootObj)) {
      const property = rootObj[propName];
      if (propName === "__ob__") continue;

      if (this.isArray(property)) {
        for (let i = 0; i < property.length; i++) {
          if (this.isHashOrArray(property[i])) {
            if (recurse) this.actOn(property[i], callback, recurse, opts);
          } else {
            callback(property, i.toString());
          }
        }
      } else if (this.isHash(property)) {
        if (opts.callbackObjects) callback(rootObj, propName)
        if (recurse) this.actOn(property, callback, recurse, opts);
      } else {
        callback(rootObj, propName);
      }
    }
  },
  transformValues(oldObj: object, transformationCallback: Function): object {
    return Object.keys(oldObj).reduce((newObj, key) => {
      newObj[key] = transformationCallback(oldObj[key]);
      return newObj;
    }, {});
  },
  /**
   * Create a new object with selected properties from another object
   *
   * Warning: If the values of the object are objects, mutations to the new object's keys will affect the old object
   *
   * @param object
   * @param keys
   */
  sliceProperties<T, K extends keyof T>(object: T, ...keys: K[]): { [key in K]: T[key] } {
    const newObj = {}
    for (const k of keys) {
      Object.assign(newObj, { [k]: object[k] })
    }
    return newObj as { [key in K]: T[key] }
  },
  destroyProperties(object: object, keys: string | string[]): void {
    this.actOn(object, (obj, propName) => {
      if (keys.includes(propName)) {
        delete object[propName];
      }
    });
  },
  cloneWithNewClientIds<T>(object: T): T {
    const clientIdMap = {};
    this.actOn(object, (obj, objPropName) => {
      const property = obj[objPropName];
      switch (objPropName) {
        case 'id':
          delete obj[objPropName];
          break;
        default:
          if (Guid.test(property)) {
            if (!clientIdMap[property]) {
              clientIdMap[property] = Guid.create();
            }
            obj[objPropName] = clientIdMap[property];
          }
          break;
      }
    });
    return object;
  },
  isHash(object: any): boolean {
    return typeof object === 'object' && object !== null && !this.isArray(object) && !(object instanceof Date);
  },
  isHashOrArray(object: any): boolean {
    return this.isHash(object) || this.isArray(object);
  },
  isArray(obj: any): obj is unknown[] {
    return Array.isArray(obj);
  },
  isDate(obj: any): obj is Date {
    return obj instanceof Date
  },
  merge(...args): any {
    if (arguments.length === 0) {
      return {};
    }
    const first = arguments[0];
    for (let i = 1; i < arguments.length; i++) {
      const other = arguments[i];
      const propertyNames = Object.getOwnPropertyNames(other);
      for (let j = 0; j < propertyNames.length; j++) {
        const propertyName = propertyNames[j];
        if (propertyName === "__ob__") continue;
        const property = other[propertyName];
        if (Array.isArray(property)) {
          if (!first[propertyName]) {
            first[propertyName] = [];
          }
          for (let k = 0; k < property.length; k++) {
            const arrayProperty = property[k];
            if (Array.isArray(arrayProperty)) {
              first[propertyName].push(this.merge([], arrayProperty));
            } else if (typeof arrayProperty === 'object') {
              first[propertyName].push(this.merge({}, arrayProperty));
            } else {
              first[propertyName].push(arrayProperty);
            }
          }
          // first[propertyName] = first[propertyName].concat(property);
        } else if (property && typeof property === 'object') {
          if (!first[propertyName]) {
            first[propertyName] = {};
          }
          this.merge(first[propertyName], property);
        } else {
          first[propertyName] = property;
        }
      }
    }
    return first;
  },
  toAttrs(rootObj: any, includedKeys: string[] = null, embeddedKeys: string[] = []): any {
    if (this.isHash(rootObj)) {
      for (const propName of Object.getOwnPropertyNames(rootObj)) {
        if (propName === "__ob__") continue;
        const value = rootObj[propName];
        if (embeddedKeys.includes(propName)) {
          const attrName = `${propName}_attributes`;
          rootObj[attrName] = value;
          delete rootObj[propName];
          this.toAttrs(rootObj[attrName], includedKeys, embeddedKeys);
        } else if (includedKeys && !includedKeys.includes(propName) && !this.isArray(rootObj)) {
          delete rootObj[propName];
        } else {
          this.toAttrs(value, includedKeys, embeddedKeys);
        }
      }
    } else if (this.isArray(rootObj)) {
      for (let i = 0; i < rootObj.length; i++) {
        if (this.isHash(rootObj[i])) {
          this.toAttrs(rootObj[i], includedKeys, embeddedKeys);
        }
      }
    }
    return rootObj;
  },
  dig(obj: any, path: string): any {
    if (!obj) return null
    if (!path) return obj

    const nodes = path.split('.')
    return nodes.reduce((context, current, index) => {
      return context?.[current]
    }, obj)
  },
  /**
   * borrowed :) from Vue JS src/util/route
   * Checks deep equivalence between 2 objects
   */
  isObjectEqual(a = {}, b = {}): boolean {
    if (!a || !b) return a === b
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) {
      return false
    }
    return aKeys.every(key => {
      const aVal = a[key]
      const bVal = b[key]
      // check nested equality
      if (typeof aVal === 'object' && typeof bVal === 'object') {
        return ObjectHelper.isObjectEqual(aVal, bVal)
      }
      return String(aVal) === String(bVal)
    })
  },
  /**
   * Checks top-level partial set of keys match
   */
  isMatch<T>(checks: T, obj: T): boolean {
    return Object.entries(checks).every(([key, value]) => {
      return obj[key] === value
    })
  },
  isAnyMatch<T>(checks: Partial<T>, ...objs: T[]): boolean {
    return objs.some(obj => {
      return ObjectHelper.isMatch(checks, obj)
    })
  },
  /**
   * this way typescript will know the correct typing
   */
  hasProperty<T, P extends PropertyKey>(obj: T, prop: P): obj is T & Record<P, unknown> {
    return typeof obj === 'object' && prop in obj
  },
  /**
   * @example repeatable checkbox with 0 instances
   *   persisted value should be []
   *   => must send [''] or "[]"
   *
   * @example repeatable checkbox with 1 empty instance
   *   persisted value should be [[]]
   *   => must send [['']]
   *
   * @example repeatable checkbox with sparse data - [[f_client_id, f_client_id_2], [], [f_client_id_3]]
   *   persisted value should be [[f_client_id, f_client_id_2], [], [f_client_id_3]]
   *   => must send [[f_client_id, f_client_id_2], [''], [f_client_id_3]]
   *
   * @see {@link objectHelper.test.ts}
   */
  prepForRails(rootObj: unknown, opts: { emptyArrayFormat?: 'string' | 'array' } = {}): any {
    opts.emptyArrayFormat = opts.emptyArrayFormat ?? 'array'
    if (this.isHash(rootObj)) {
      const obj = {}
      for (const propName of Object.keys(rootObj)) {
        obj[propName] = this.prepForRails(rootObj[propName], opts)
      }
      return obj
    } else if (this.isArray(rootObj)) {
      const v = ArrayHelper.prepForRails(rootObj as unknown[], { emptyFormat: opts.emptyArrayFormat })
      if (!this.isArray(v)) return v

      const vv = (<unknown[]>v).map(x => this.prepForRails(x, { emptyFormat: opts.emptyArrayFormat }))
      /**
       * if there is a mix of empty and populated sub-arrays, then make sure all array items are of same type or Rails will complain
       * can happen for repeatable checkbox field when first checkbox instance is blank and second is populated
       */
      if (vv.length > 1 && vv.some(x => this.isArray(x)) && vv.some(x => x === '[]')) return vv.map(vvv => vvv === '[]' ? [''] : vvv)

      return vv
    } else if (this.isDate(rootObj)) {
      // specifically using toString() and not toISOString() to support a test
      // features/form_filler/form_submissions/formulas/step_duration_fields.feature
      return rootObj.toString()
    } else {
      return rootObj
    }
  },
};
